import { useState, useEffect } from 'react';
import { Campaign } from '../models/Campaign';
import { CampaignService } from '../services/firebase';

export const useCampaigns = () => {
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Set up real-time listener for campaigns
        const unsubscribe = CampaignService.getCampaigns((updatedCampaigns) => {
            setCampaigns(updatedCampaigns);
            setLoading(false);
        });

        // Clean up listener on unmount
        return () => unsubscribe();
    }, []);

    const fetchCampaignById = async (id: string): Promise<Campaign | null> => {
        try {
            return await CampaignService.getCampaignById(id);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            return null;
        }
    };

    const uploadImage = async (file: File, campaignId: string): Promise<string | null> => {
        try {
            return await CampaignService.uploadImage(file, campaignId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to upload image');
            return null;
        }
    };

    const uploadVideo = async (
        file: File, 
        campaignId: string, 
        progressCallback: (progress: number) => void
    ): Promise<string | null> => {
        try {
            return await CampaignService.uploadVideo(file, campaignId, progressCallback);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to upload video');
            return null;
        }
    };

    const createCampaign = async (campaign: Omit<Campaign, 'id'>): Promise<string | null> => {
        try {
            return await CampaignService.createCampaign(campaign);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create campaign');
            return null;
        }
    };

    return {
        campaigns,
        loading,
        error,
        fetchCampaignById,
        uploadImage,
        uploadVideo,
        createCampaign
    };
}; 